export type PricingCurrency = "INR" | "USD"
export type PlanName = keyof typeof PLANS
export type PlanType = "weekly" | "monthly" | "yearly" | "credit-pack"
export type PlanCategory =
  | "credit-pack"
  | "subscription"
  | "mock-credit-pack"
  | "human-interview-pack"
export type HeaderLookup = Pick<Headers, "get">

// Sentinel value indicating "unlimited" interview call credits.
// Stored as NULL in the DB (see lib/supabase/credit-manager.ts).
export const UNLIMITED_INTERVIEW_CALL_CREDITS = -1

// Temporary payment-gateway incident mode. Keep every temporary commercial
// rule behind this single object so an incident response is one flag flip.
// Disabled 2026-08-06 after the Razorpay → Dodo Payments migration went live;
// the machinery stays so a future gateway incident is a one-line response.
export const TEMPORARY_PAYMENT_GATEWAY_MODE = {
  enabled: false,
  availablePlan: "monthly",
  accessDays: 7,
  campaignId: "payment-gateway-incident-2026-08-06",
} as const

export const isTemporaryAccessPlan = (planName: string | null | undefined) =>
  TEMPORARY_PAYMENT_GATEWAY_MODE.enabled &&
  planName?.toLowerCase() === TEMPORARY_PAYMENT_GATEWAY_MODE.availablePlan

export const isPlanSoldOut = (planName: string | null | undefined) =>
  TEMPORARY_PAYMENT_GATEWAY_MODE.enabled && !isTemporaryAccessPlan(planName)

// Add whole months to an instant in UTC, keeping the time of day and clamping
// to the last day of a short target month.
//
// This replaces `new Date(y, m + n, d)`, which was wrong three ways: it built
// LOCAL MIDNIGHT, so the purchase time of day was discarded and the customer
// silently lost up to 24 h of the window they paid for (a 23:55 purchase lost
// nearly a full day); it overflowed on month ends (31 Jan + 1 month produced
// 3 March, ~31 free days); and being local it moved with the deployment's TZ
// and landed on a DST-ambiguous instant twice a year.
const addUtcMonths = (from: Date, months: number): Date => {
  const year = from.getUTCFullYear()
  const month = from.getUTCMonth() + months
  // Day 0 of the month after the target = the target month's last day.
  const daysInTargetMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  return new Date(
    Date.UTC(
      year,
      month,
      Math.min(from.getUTCDate(), daysInTargetMonth),
      from.getUTCHours(),
      from.getUTCMinutes(),
      from.getUTCSeconds(),
      from.getUTCMilliseconds()
    )
  )
}

export const getPlanAccessEndDate = (planType: string, now: Date = new Date()) => {
  const normalized = planType.toLowerCase()
  if (normalized === "credit-pack") {
    // Packs never expire; 100 years stands in for "no end".
    return addUtcMonths(now, 1200)
  }
  if (normalized === "yearly") {
    return addUtcMonths(now, 12)
  }
  if (normalized === "weekly") {
    return new Date(now.getTime() + 7 * 86_400_000)
  }
  if (normalized === "monthly" && TEMPORARY_PAYMENT_GATEWAY_MODE.enabled) {
    return new Date(now.getTime() + TEMPORARY_PAYMENT_GATEWAY_MODE.accessDays * 86_400_000)
  }
  return addUtcMonths(now, 1)
}

export const isUnlimitedInterviewCallCredits = (value: number | null | undefined) =>
  value == null || value < 0

// Plans that have unlimited usage credits AND interview-call credits — no
// deduction is performed for these. Used by both the deduct-credit and
// deduct-interview-call-credit code paths. Active state is gated by
// `end_date` separately (an expired subscription is no longer unlimited).
const UNLIMITED_PLAN_NAMES = new Set(["weekly", "monthly", "yearly"])

export const isUnlimitedPlan = (planName: string | null | undefined) =>
  !!planName && UNLIMITED_PLAN_NAMES.has(planName.toLowerCase())

// Credit-pack plans never expire — `end_date` is intentionally ignored when
// deducting from these. Credits are the only gate.
const CREDIT_PACK_PLAN_NAMES = new Set(["starter", "basic", "power"])

export const isCreditPackPlan = (planName: string | null | undefined) =>
  !!planName && CREDIT_PACK_PLAN_NAMES.has(planName.toLowerCase())

// ── Derived subscription state ───────────────────────────────────────
// The subscriptions.status column is write-once: grant_credits (and the
// free-signup insert) set it to 'active' and nothing ever flips it, so it
// reads 'active' forever — including for monthly/yearly plans whose window
// lapsed long ago. NEVER gate anything on the stored column. Derive the
// effective status from plan_name + end_date instead:
//   - monthly/yearly: active while end_date is in the future
//   - credit packs (starter/basic/power): lifetime by design — credits are
//     the only gate, so the stored status passes through
//   - free/unknown: stored status passes through (not a subscription)

export type SubscriptionStatusFields = {
  status?: string | null
  plan_name?: string | null
  end_date?: string | null
}

export const isSubscriptionWindowExpired = (endDate: string | Date | null | undefined) => {
  if (!endDate) return false
  const ts = new Date(endDate).getTime()
  return Number.isFinite(ts) && ts <= Date.now()
}

export const deriveSubscriptionStatus = (
  sub: SubscriptionStatusFields | null | undefined
): string | null => {
  if (!sub) return null
  if (isUnlimitedPlan(sub.plan_name)) {
    if (isSubscriptionWindowExpired(sub.end_date)) return "expired"
    // In-window: a stale stored 'expired' (row lapsed once, then the plan
    // was renewed/extended) must not survive — the window is authoritative.
    // Other explicit stored values (e.g. an admin-set 'canceled') pass
    // through so manual overrides remain visible.
    return sub.status === "expired" || sub.status == null ? "active" : sub.status
  }
  return sub.status ?? null
}

// True while a monthly/yearly plan window is still open. This is the
// paid-subscriber check the deduct paths already enforce; UI and API layers
// must use the same rule so what users see matches what the server allows.
export const isActiveUnlimitedSubscription = (
  sub: Pick<SubscriptionStatusFields, "plan_name" | "end_date"> | null | undefined
) => !!sub && isUnlimitedPlan(sub.plan_name) && !isSubscriptionWindowExpired(sub.end_date)

// PostgREST .or() fragment for DB-level queries that previously relied on
// `.eq("status", "active")` alone. Combined (AND) with that stored-status
// check it reproduces deriveSubscriptionStatus === "active": a row passes
// unless it is an unlimited plan whose end_date has lapsed. NULL end_date
// counts as not-expired, mirroring isSubscriptionWindowExpired.
export const activePlanWindowOrFilter = (now: Date = new Date()) =>
  `plan_name.not.in.(weekly,monthly,yearly),end_date.gt.${now.toISOString()},end_date.is.null`

// Which plans carry the "Most Popular" badge.
//
// This lives in the catalog rather than on a page's card config because it is
// rendered on more than one surface. It was previously declared only in the
// pricing page's local card list, and the checkout page hard-coded its own
// guess — which drifted: pricing badged `monthly`, checkout badged `yearly`,
// so a shopper was told a different plan was most popular depending on which
// page they were looking at. One source, read by both.
export const POPULAR_PLANS: readonly string[] = ["basic", "monthly"]

export const isPopularPlan = (planName: string | null | undefined): boolean =>
  !!planName && POPULAR_PLANS.includes(planName.toLowerCase())

export const PLANS = {
  // ===== Subscription =====
  weekly: {
    name: "weekly",
    price: 2499,
    credits: 20,
    interviewCallCredits: UNLIMITED_INTERVIEW_CALL_CREDITS,
    mockInterviewCredits: 1,
    humanInterviewCredits: 0,
    planType: "weekly",
    category: "subscription",
  },
  monthly: {
    name: "monthly",
    price: 4299,
    credits: 75,
    interviewCallCredits: UNLIMITED_INTERVIEW_CALL_CREDITS,
    // Each subscription tier includes 1 free AI mock interview, granted at
    // purchase time by grant_credits() (which increments the
    // mock_interview_credits column) — same flow as the mock-credit packs,
    // see lib/payments/process-payment.ts. Extra sessions are sold
    // separately (mock_single / mock_sprint / mock_prep / mock_marathon).
    mockInterviewCredits: 1,
    humanInterviewCredits: 0,
    planType: "monthly",
    category: "subscription",
  },
  yearly: {
    name: "yearly",
    price: 19999,
    credits: 1500,
    interviewCallCredits: UNLIMITED_INTERVIEW_CALL_CREDITS,
    mockInterviewCredits: 1,
    humanInterviewCredits: 0,
    planType: "yearly",
    category: "subscription",
  },
  // ===== Credit packs (one-time, never expire) =====
  // Marketing breakdown of `credits` (the single fungible text-credit
  // counter): Starter = 20 online-round + 10 free = 30. Basic = 60 +
  // 20 free = 80. Power = 90 + 60 free = 150. All three counts are
  // already merged into the `credits` field below — the "+ N free"
  // split exists only for promotional copy on the pricing page.
  // Credit packs don't grant mock-interview credits (yet).
  starter: {
    name: "starter",
    price: 1599,
    credits: 30,
    interviewCallCredits: 3,
    mockInterviewCredits: 0,
    humanInterviewCredits: 0,
    planType: "credit-pack",
    category: "credit-pack",
  },
  basic: {
    name: "basic",
    price: 2599,
    credits: 80,
    interviewCallCredits: 8,
    mockInterviewCredits: 0,
    humanInterviewCredits: 0,
    planType: "credit-pack",
    category: "credit-pack",
  },
  power: {
    name: "power",
    price: 3599,
    credits: 150,
    interviewCallCredits: 15,
    mockInterviewCredits: 0,
    humanInterviewCredits: 0,
    planType: "credit-pack",
    category: "credit-pack",
  },
  // ===== Mock-interview credit packs (one-time, never expire) =====
  // Sold separately from text/call credit packs and subscriptions.
  // Each pack grants `mockInterviewCredits` worth of 30-min sessions.
  // Balance never resets — credits ADD to the user's existing balance
  // on every purchase (see credit-manager.ts).
  mock_single: {
    name: "mock_single",
    price: 249,
    credits: 0,
    interviewCallCredits: 0,
    mockInterviewCredits: 1,
    humanInterviewCredits: 0,
    planType: "credit-pack",
    category: "mock-credit-pack",
  },
  mock_sprint: {
    name: "mock_sprint",
    price: 999,
    credits: 0,
    interviewCallCredits: 0,
    mockInterviewCredits: 5,
    humanInterviewCredits: 0,
    planType: "credit-pack",
    category: "mock-credit-pack",
  },
  mock_prep: {
    name: "mock_prep",
    price: 1499,
    credits: 0,
    interviewCallCredits: 0,
    mockInterviewCredits: 15,
    humanInterviewCredits: 0,
    planType: "credit-pack",
    category: "mock-credit-pack",
  },
  mock_marathon: {
    name: "mock_marathon",
    price: 2499,
    credits: 0,
    interviewCallCredits: 0,
    mockInterviewCredits: 30,
    humanInterviewCredits: 0,
    planType: "credit-pack",
    category: "mock-credit-pack",
  },
  // ===== 1-1 Human interview packs (one-time, never expire) =====
  // Sold separately from AI mock interviews. Each "credit" represents a
  // scheduled 60-minute session with a human interviewer (booking flow
  // handled out-of-band — clicking the CTA initiates a contact/booking
  // request). Credits never expire and ADD to the user's balance.
  human_single: {
    name: "human_single",
    price: 499,
    credits: 0,
    interviewCallCredits: 0,
    mockInterviewCredits: 0,
    humanInterviewCredits: 1,
    planType: "credit-pack",
    category: "human-interview-pack",
  },
  human_pack3: {
    name: "human_pack3",
    price: 1299,
    credits: 0,
    interviewCallCredits: 0,
    mockInterviewCredits: 0,
    humanInterviewCredits: 3,
    planType: "credit-pack",
    category: "human-interview-pack",
  },
  human_pack5: {
    name: "human_pack5",
    price: 1999,
    credits: 0,
    interviewCallCredits: 0,
    mockInterviewCredits: 0,
    humanInterviewCredits: 5,
    planType: "credit-pack",
    category: "human-interview-pack",
  },
} as const

// Lookup helper used by the mock-interview deduct flow + quota endpoint.
// Returns 0 for unknown plan names (so unauthenticated/free/expired users
// can't run mock interviews).
export function getMockInterviewCreditsForPlan(planName: string | null | undefined): number {
  if (!planName) return 0
  const plan = PLANS[planName.toLowerCase() as PlanName]
  return plan?.mockInterviewCredits ?? 0
}

export function getHumanInterviewCreditsForPlan(planName: string | null | undefined): number {
  if (!planName) return 0
  const plan = PLANS[planName.toLowerCase() as PlanName]
  return plan?.humanInterviewCredits ?? 0
}

// Site-wide launch promotion. `GETJOB50` is auto-applied at checkout and shown
// on every pricing card. The strike-through ("slashed") price is NOT a hand-
// tuned marketing rack rate — it is derived from the real sale price below by
// grossing it back up by exactly this discount. That guarantees one honest
// invariant everywhere: applying GETJOB50 to the slashed price reproduces the
// sale price the customer expects to pay. Change the discount here and the
// slashed prices, the "% OFF" badges, and the checkout math all move together.
export const PROMO_COUPON = {
  code: "GETJOB50",
  percentOff: 50,
  // The factor the sale price is grossed back up by: 100 / (100 - percentOff).
  // Stored rather than computed so the slashed price is integer arithmetic —
  // deriving it would put a float in the middle of a price. A test pins the two
  // against each other so they cannot drift.
  grossUp: 2,
} as const

export type PromoCoupon = typeof PROMO_COUPON

// Which promo a plan's sticker price is measured against. Anything absent falls
// back to the site-wide PROMO_COUPON.
//
// Deliberately EMPTY. The annual plan used to carry its own deeper code
// (INTERVIEW75, 75% off a ₹79,999 rack rate) so that yearly was measured
// against a different sticker from the rest of the site. That was retired on
// 2026-08-22: yearly now uses the site-wide GETJOB50 like every other plan, so
// its rack rate halves from `4×sale+3` to `2×sale+1` (₹79,999 → ₹39,999,
// $799 → $399) while the price actually charged is unchanged at ₹19,999/$199.
//
// The map is kept rather than deleted because the per-plan mechanism is what
// makes `isPromoCouponForPlan` — the money guard in create-checkout — plan
// aware. Reintroducing a plan-specific promo means adding one entry here and
// nothing else.
const PLAN_PROMO_COUPON: Partial<Record<PlanName, PromoCoupon>> = {}

export const getPromoCouponForPlan = (planName?: string | null): PromoCoupon =>
  PLAN_PROMO_COUPON[(planName ?? "").toLowerCase() as PlanName] ?? PROMO_COUPON

// Every promo code the site issues, for membership checks.
export const ALL_PROMO_COUPONS: readonly PromoCoupon[] = [PROMO_COUPON]

// The plans a given promo code is the DEFAULT for — i.e. the plans whose
// checkout auto-applies it and whose sticker price is measured against it.
//
// This is NOT redeemability. GETJOB50 remains valid on every plan including
// yearly (coupons.applicable_plans is NULL); it simply is not what the yearly
// checkout auto-applies. A customer who types GETJOB50 on the annual plan is
// accepted and pays the advertised price, because the advertised-price cap in
// create-checkout is plan-blind.
export const getPlansWithPromoDefault = (code: string): PlanName[] =>
  (Object.keys(PLANS) as PlanName[]).filter(
    (plan) => getPromoCouponForPlan(plan).code === code.trim().toUpperCase()
  )

// Slashed (strike-through) price = the rack rate the discount is measured
// against, e.g. a $49 sale shows as ~~$99~~ and a $199 sale as ~~$399~~. Every
// plan is now measured against the site-wide 50% promo, so in practice the
// factor is `2× sale + 1` everywhere — but it is still read from the plan's
// promo rather than hard-coded, so reintroducing a plan-specific rate is one
// PLAN_PROMO_COUPON entry and no changes here. Whichever factor applies, taking
// `percentOff` off the result leaves a fraction that getNetPrice rounds DOWN,
// landing back exactly on the sale price — so what the customer pays never
// changes, only the sticker the discount is measured against.
//
// Note this yields a charm price ending in 9 only when the sale price itself
// ends in 9 ($49 → $99, $199 → $399). Sale prices that don't (e.g.
// mock_sprint $34 → $69) still satisfy the floor-back invariant, which is the
// property that actually matters; the trailing digit is cosmetic.
export const getSlashedPrice = (amount: number, planName?: string | null): number => {
  const { grossUp } = getPromoCouponForPlan(planName)
  // sale * k + (k - 1): the +(k-1) is what makes the discount land back EXACTLY
  // on the sale price after getNetPrice floors. 50% -> 2*sale+1
  // (19,999 -> 39,999 -> 19,999.5 -> 19,999). A hypothetical 75% would be
  // 4*sale+3 — same invariant, one factor apart.
  return amount * grossUp + (grossUp - 1)
}

// Price actually payable after a discount, rounded DOWN to a whole unit (in the
// customer's favour). Used by both the checkout UI and the order route so the
// displayed total and the charged amount always agree. Floor — not round — so
// the .5 from a 50% coupon on an odd slashed price drops (₹49.5 → ₹49).
export const getNetPrice = (base: number, discount: number): number =>
  Math.max(0, Math.floor(base - discount))

// `amount` is the real sale price — the post-promo price the customer pays
// once GETJOB50 is applied, and the figure shown big on every card. The
// pre-promo strike-through is derived via getSlashedPrice (see PROMO_COUPON).
const DISPLAY_PLAN_PRICES: Record<PlanName, Record<PricingCurrency, { amount: number }>> = {
  weekly: { INR: { amount: 2499 }, USD: { amount: 29 } },
  monthly: { INR: { amount: 4299 }, USD: { amount: 49 } },
  yearly: { INR: { amount: 19999 }, USD: { amount: 199 } },
  starter: { INR: { amount: 1599 }, USD: { amount: 19 } },
  basic: { INR: { amount: 2599 }, USD: { amount: 29 } },
  power: { INR: { amount: 3599 }, USD: { amount: 39 } },
  mock_single: { INR: { amount: 249 }, USD: { amount: 9 } },
  mock_sprint: { INR: { amount: 999 }, USD: { amount: 34 } },
  mock_prep: { INR: { amount: 1499 }, USD: { amount: 89 } },
  mock_marathon: { INR: { amount: 2499 }, USD: { amount: 149 } },
  human_single: { INR: { amount: 499 }, USD: { amount: 19 } },
  human_pack3: { INR: { amount: 1299 }, USD: { amount: 49 } },
  human_pack5: { INR: { amount: 1999 }, USD: { amount: 79 } },
}

export const getPlanDetails = (planName: string) => {
  const key = planName.toLowerCase() as PlanName
  return PLANS[key] || null
}

export const getPlanDisplayPricing = (planName: string, currency: PricingCurrency) => {
  const key = planName.toLowerCase() as PlanName
  const entry = DISPLAY_PLAN_PRICES[key]?.[currency]
  if (!entry) return null
  // `amount` is the promo (sale) price; `originalAmount` is the pre-promo
  // strike-through, derived so GETJOB50 off it lands back on `amount`.
  return {
    amount: entry.amount,
    originalAmount: entry.amount === 0 ? 0 : getSlashedPrice(entry.amount, key),
  }
}

// ── Promo integrity guard ────────────────────────────────────────────
// The advertised price is not stored anywhere the customer can see it being
// enforced: checkout charges the SLASHED price and only lands on the
// advertised figure because PROMO_COUPON takes its cut back off — and that
// coupon is an ordinary, admin-editable row in `public.coupons`. Nothing in
// the DB knows it underpins every price on /pricing, the schema.org Offer and
// all marketing copy. Editing its discount_value therefore silently bills
// customers a different number than the site published.
//
// So the invariant is enforced here instead, on the amount itself: whatever
// the coupon row says today, a promo checkout may never charge ABOVE the
// advertised price. Charging below it is a legitimately deeper promo and is
// left alone (still reported so an unintended edit is visible in the logs).
export type PromoPricingCheck = {
  // The amount that may actually be charged — capped at the advertised price.
  amount: number
  advertised: number
  mismatch: "none" | "overcharge" | "undercharge"
}

export const reconcilePromoPricing = (
  planName: string,
  currency: PricingCurrency,
  chargedAmount: number,
  fallbackPrice: number
): PromoPricingCheck => {
  const advertised = getPlanDisplayPricing(planName, currency)?.amount ?? fallbackPrice
  if (chargedAmount > advertised) {
    return { amount: advertised, advertised, mismatch: "overcharge" }
  }
  if (chargedAmount < advertised) {
    return { amount: chargedAmount, advertised, mismatch: "undercharge" }
  }
  return { amount: chargedAmount, advertised, mismatch: "none" }
}

// True for ANY code the site itself advertises. Callers that only need "is this
// one of ours" (the checkout's advertised-price reconciliation) use this.
export const isPromoCoupon = (couponCode: string | null | undefined) =>
  !!couponCode && ALL_PROMO_COUPONS.some((c) => c.code === couponCode.trim().toUpperCase())

// True only when the code is the one THIS plan advertises. With a single
// site-wide promo this is currently true for every plan, but the distinction is
// load-bearing and must not be collapsed into isPromoCoupon: a plan-specific
// code redeemed against the wrong plan must not be treated as that plan's
// advertised promo, because that would reconcile the plan down to a price it is
// not sold at. The DB row carries applicable_plans as the authoritative guard;
// this is the same rule in the pricing layer.
export const isPromoCouponForPlan = (
  couponCode: string | null | undefined,
  planName: string | null | undefined
) => !!couponCode && couponCode.trim().toUpperCase() === getPromoCouponForPlan(planName).code

export const getPlansByCategory = (category: PlanCategory): PlanName[] => {
  if (category === "subscription") {
    return ["weekly", "monthly", "yearly"]
  }
  if (category === "mock-credit-pack") {
    return ["mock_single", "mock_sprint", "mock_prep", "mock_marathon"]
  }
  if (category === "human-interview-pack") {
    return ["human_single", "human_pack3", "human_pack5"]
  }
  return ["starter", "basic", "power"]
}

export const getCountryCodeFromHeaders = (headers: HeaderLookup) => {
  const countryCode = [
    headers.get("x-vercel-ip-country"),
    headers.get("cf-ipcountry"),
    headers.get("cloudfront-viewer-country"),
    headers.get("x-country-code"),
  ]
    .find(Boolean)
    ?.toUpperCase()

  if (countryCode) {
    return countryCode
  }

  return null
}

export const getPricingCurrencyFromCountryCode = (countryCode?: string | null): PricingCurrency => {
  return countryCode?.trim().toUpperCase() === "IN" ? "INR" : "USD"
}

export const getPricingCurrencyFromHeaders = (headers: HeaderLookup): PricingCurrency => {
  return getPricingCurrencyFromCountryCode(getCountryCodeFromHeaders(headers))
}

export const formatPlanPrice = (amount: number, currency: PricingCurrency) => {
  return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount)
}
